// Backend Upload utilities for entity creation using Flask backend

import { BACKEND_CONFIG } from '../config/index';

export interface BackendUploadResult {
  cid: string;
  url: string;
  filename: string;
  size: number;
}

export interface BackendHealthResult {
  status: string;
  timestamp: string;
  service: string;
  version: string;
}

/**
 * Check if the backend server is healthy
 */
export async function checkBackendHealth(): Promise<BackendHealthResult | null> {
  try {
    const response = await fetch(`${BACKEND_CONFIG.URL}/api/health`);
    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch (error) {
    console.error('Backend health check failed:', error);
    return null;
  }
}

/**
 * Upload a file to IPFS using the backend API
 */
export async function uploadFileToBackend(
  content: string | File,
  filename: string
): Promise<BackendUploadResult> {
  const backendUrl = BACKEND_CONFIG.URL;
  
  try {
    // Create FormData
    const formData = new FormData();
    
    if (typeof content === 'string') {
      // Create a File from string content
      const file = new File([content], filename, {
        type: getMimeType(filename)
      });
      formData.append('file', file);
    } else {
      // Content is already a File
      formData.append('file', content);
    }
    
    console.log(`Uploading file to backend: ${filename}`);
    
    // Upload to backend
    const response = await fetch(`${backendUrl}${BACKEND_CONFIG.UPLOAD_ENDPOINT}`, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Backend upload failed: ${response.status} ${response.statusText} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log('Backend upload result:', result);
    
    if (!result.success) {
      throw new Error(result.error || 'Backend upload failed');
    }
    
    return {
      cid: result.data.cid,
      url: result.data.url,
      filename: result.data.filename,
      size: result.data.size
    };
    
  } catch (error) {
    console.error('Backend upload error:', error);
    throw new Error(`Failed to upload to backend: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Upload multiple files to IPFS using the backend API
 */
export async function uploadFilesToBackend(
  files: { [filename: string]: string | File }
): Promise<BackendUploadResult[]> {
  const results: BackendUploadResult[] = [];
  
  for (const [filename, content] of Object.entries(files)) {
    try {
      const result = await uploadFileToBackend(content, filename);
      results.push(result);
    } catch (error) {
      console.error(`Failed to upload ${filename}:`, error);
      throw error;
    }
  }
  
  return results;
}

/**
 * Upload multiple files as a directory to IPFS using the backend API
 */
export async function uploadDirectoryToBackend(
  files: { [filename: string]: string | File }
): Promise<BackendUploadResult> {
  const backendUrl = BACKEND_CONFIG.URL;
  
  try {
    // Create FormData with multiple files
    const formData = new FormData();
    
    for (const [filename, content] of Object.entries(files)) {
      if (typeof content === 'string') {
        // Create a File from string content
        const file = new File([content], filename, {
          type: getMimeType(filename)
        });
        formData.append('files', file);
      } else {
        // Content is already a File
        formData.append('files', content);
      }
    }
    
    console.log(`Uploading directory to backend with ${Object.keys(files).length} files`);
    
    // Upload to backend directory endpoint
    const response = await fetch(`${backendUrl}${BACKEND_CONFIG.UPLOAD_DIRECTORY_ENDPOINT}`, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Backend directory upload failed: ${response.status} ${response.statusText} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log('Backend directory upload result:', result);
    
    if (!result.success) {
      throw new Error(result.error || 'Backend directory upload failed');
    }
    
    return {
      cid: result.data.cid,
      url: result.data.url,
      filename: 'directory',
      size: result.data.totalSize
    };
    
  } catch (error) {
    console.error('Backend directory upload error:', error);
    throw new Error(`Failed to upload directory to backend: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Upload entity page HTML with metadata to IPFS using the backend
 */
export async function uploadEntityPageToBackend(
  htmlContent: string,
  entityName: string,
  entityAddress?: string
): Promise<BackendUploadResult> {
  // Import the metadata generator
  const { createIPFSMetadata } = await import('./metadataGenerator');
  const { serializeWithBigIntSupport } = await import('./formatting');
  
  // Create the metadata
  const metadata = createIPFSMetadata(
    'entity',
    {
      'index.html': {
        type: 'html',
        path: 'index.html'
      }
    },
    'index.html',
    entityAddress
  );
  
  // Prepare files for directory upload
  const files = {
    'index.html': htmlContent,
    'metadata.json': serializeWithBigIntSupport(metadata)
  };
  
  // Upload as a directory (both files will share the same CID)
  const result = await uploadDirectoryToBackend(files);
  
  console.log('Entity page uploaded with metadata as directory:', {
    result,
    files: Object.keys(files),
    metadata: metadata
  });
  
  return result;
}

/**
 * Test backend connection
 */
export async function testBackendConnection(): Promise<boolean> {
  const backendUrl = BACKEND_CONFIG.URL;
  
  try {
    const response = await fetch(`${backendUrl}${BACKEND_CONFIG.HEALTH_ENDPOINT}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const result = await response.json();
      return result.status === 'ok';
    }
    
    return false;
  } catch (error) {
    console.warn('Backend connection test failed:', error);
    return false;
  }
}

/**
 * Get MIME type for a filename
 */
function getMimeType(filename: string): string {
  const extension = filename.split('.').pop()?.toLowerCase();
  
  switch (extension) {
    case 'html':
    case 'htm':
      return 'text/html';
    case 'css':
      return 'text/css';
    case 'js':
      return 'application/javascript';
    case 'json':
      return 'application/json';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'svg':
      return 'image/svg+xml';
    case 'webp':
      return 'image/webp';
    default:
      return 'application/octet-stream';
  }
} 